import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CalculatorOutlined,
  CrownOutlined,
  DeleteOutlined,
  EuroCircleOutlined,
  GlobalOutlined,
  ShopOutlined,
  TabletOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import collect from 'collect.js';
import { Badge } from 'antd';
import i18n from 'i18next';
import {
  BarChart3,
  Bug,
  Clock,
  Code2,
  CreditCard,
  FileSignature,
  FileText,
  Flag,
  FlaskConical,
  Form,
  Gift,
  Handshake,
  Home,
  ListChecks,
  Plus,
  Rocket,
  Settings,
  Sparkles,
  Store,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { navItemIsVisible } from './navItemVisibility';
import usePlgOnboarding from '../auth/usePlgOnboarding';
import {
  isFranchiseSyncHidden,
  isPlgOnboardingProvider,
  isWebsiteStudioAvailable,
} from '../../services/tenant/plgOnboarding';
import { CompanyOperationalStatus, ENVIRONMENTS, FeatureFlags } from '../../services/exports/Constants';
import { setsStripeDestinationRates } from '../../services/helpers/StripeProcessingFees';
import { handlesMissedOrdersAsPartner } from '../../services/tenant/providerBilling';
import useVerification from '../verification/useVerification';
import usePermissions, { ManagerPermission, ManagerRole } from '../auth/usePermissions';
import TableIcon from '../../resources/assets/icons/TableIcon';
import MarketingIcon from '../../resources/assets/icons/MarketingIcon';
import { ScreenEnum, ScreenGroupEnum } from './Screens';
import { selectCurrentFranchiseId } from '../../store/slices/CurrentFranchiseSlice';
import { availableDeliveryProviders } from '../../services/tenant/serviceProviderConfig';

export default function useNavigation() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Hooks:useNavigation' });

  const {
    isComplete: isOnboardingComplete,
    getRemainingSteps,
    showsGetStartedAfterOnboarding,
    missingDocuments,
  } = useVerification();
  const {
    roles,
    isSuperAdmin,
    isAdmin,
    isOnboardingManager,
    canAccessScreenGroup,
    canAccessScreen,
    can,
    isMerchant,
    hasAnyRole,
    hasGlobalAccess,
  } = usePermissions();

  const { company } = useSelector((state) => state.currentCompany);
  // Partners get the PLG onboarding wizard in place of the Get Started
  // stepper; master-provider merchants keep Get Started exactly as-is.
  const isPlgPartner = usePlgOnboarding();
  // The checklist stays after go-live (the bank account step follows it); the dot
  // marks only a restaurant that is not live yet.
  const isLive = company?.operational_status === CompanyOperationalStatus.Live;

  // Custom store SEO is internal-only, except store-role managers whose company
  // has the custom_store_seo feature flag enabled (mirrors the backend gate).
  const canManageStoreSeo =
    can(ManagerPermission.ManageStorefrontSeo) ||
    (hasAnyRole([ManagerRole.StoreAdmin, ManagerRole.StoreOperator, ManagerRole.StoreEmployee], false) &&
      company?.enabled_feature_flags?.includes(FeatureFlags.CustomStoreSeo));
  const selectedFranchiseId = useSelector(selectCurrentFranchiseId);
  const hasSelectedFranchise = isMerchant ? (selectedFranchiseId ?? company?.franchise_id) : company?.franchise_id;
  // Every restaurant has a franchise — the backend provisions a solo one for
  // standalone restaurants — so "has a franchise" is not the same as "is part of
  // a multi-location brand". See `isFranchiseSyncHidden` for the rule.
  const isMultiLocationBrand = (company?.franchise?.companies?.length ?? 0) > 1;
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  // Mirrors the backend merchant_billing gate: a non-billing provider settles
  // with its restaurants outside the platform, so subscription/invoice
  // and income-report surfaces are hidden while settlement (payouts,
  // statements, sales summary) stays.
  const hasMerchantBilling = serviceProvider?.enabled_feature_flags?.includes(FeatureFlags.MerchantBilling);
  const issuesEndCustomerReceipts = serviceProvider?.enabled_feature_flags?.includes(
    FeatureFlags.IssueEndCustomerReceipts,
  );
  // Self-serve (PLG) partner onboarding: FoodAmigos-only tooling and surfaces
  // that are empty or not yet applicable before go-live are hidden for the
  // partner and its restaurants (decided 2026-09-04). Route guard:
  // HasNotPlgOnboardingGuard. No global-access bypass.
  const isPlg = isPlgOnboardingProvider(serviceProvider);
  // Only a destination partner on its own Stripe platform sets its own Stripe processing fees.
  const managesStripeProcessingFees =
    setsStripeDestinationRates(serviceProvider) && can(ManagerPermission.WriteProcessingFeeRates);
  // PLG rule of thumb (2026-09-04): provider-level ("global SP") tabs are only
  // offered while no restaurant is selected; with one selected the sidebar is
  // about that restaurant. Applies to Partner settings / Billing / Tax & currency.
  const hidePlgPartnerTabs = isPlg && Boolean(company?.id);
  // The selected restaurant's own provider, whoever is acting: the imprint pages
  // belong to the restaurant (HasPlgOnboardingRestaurantGuard).
  const selectedRestaurantIsPlg = Boolean(company?.id) && isPlgOnboardingProvider(company?.service_provider);
  // Gift Cards is filed under Marketing, but store roles are not granted that
  // screen group, so for them the item falls back to its old home under Home.
  // The two entries are mutually exclusive on this one boolean.
  const showGiftCardsUnderHome = !canAccessScreenGroup(ScreenGroupEnum.Marketing);
  const { badges: reduxBadges } = useSelector((state) => state.sideBar);
  // Once onboarding is done, Get Started only asks for documents, so it counts the missing ones;
  // nothing left shows no number (Nishita).
  const remainingSteps = isOnboardingComplete ? missingDocuments : getRemainingSteps();
  const badges = useMemo(
    () => ({
      ...reduxBadges,
      [ScreenEnum.GetStarted]: remainingSteps,
    }),
    [reduxBadges, remainingSteps],
  );

  const items = useMemo(
    () => [
      {
        key: ScreenGroupEnum.OnlineShop,
        group: t('groups.home'),
        icon: <ShopOutlined />,
        children: [
          {
            // Master-provider Get Started — the classic stepper with the legacy KYC
            // forms. Hidden for the whole PLG provider, not only its staff: an owner
            // invited by the partner onboards on the provider's hosted page too. Once
            // onboarding is done it stays for the restaurants that keep it (PROD-543),
            // as the place to see and add documents.
            hidden: !company?.id || (isOnboardingComplete && !showsGetStartedAfterOnboarding) || isPlg,
            label: t('get_started.title'),
            key: ScreenEnum.GetStarted,
            badge: {
              color: 'primary',
              text: badges?.[ScreenEnum.GetStarted] > 0 ? badges?.[ScreenEnum.GetStarted] : null,
            },
          },
          {
            // Partner creation entry. Sits above Home and only while no restaurant
            // is selected — with one selected the Onboarding item below takes over,
            // so the sidebar never offers two competing "new restaurant" actions.
            hidden: Boolean(company?.id) || !isPlgPartner,
            label: t('new_restaurant.title'),
            key: ScreenEnum.NewRestaurant,
            icon: <Plus size={16} />,
          },
          {
            label: 'Home',
            key: ScreenEnum.Dashboard,
            icon: <Home size={16} />,
          },
          {
            // Partner replacement for Get Started: resumes the launch checklist
            // for the selected restaurant at its first incomplete phase. It stays
            // once live orders are on — the payout bank account comes after Go
            // Live, and the steps stay there to review and change.
            //
            // Sits directly below Home (Artur's feedback §1): it is the second
            // item, above Orders. The trailing dot is the same plain blue circle
            // Inbox uses — a notification dot, not the accent colour, not a pill
            // and not a count — and it is present while the restaurant is not live.
            hidden: !company?.id || !isPlgPartner,
            label: (
              <span className="flex items-center gap-xs">
                {t('onboarding.title')}
                {!isLive && (
                  <span className="h-[9px] w-[9px] shrink-0 rounded-full bg-brand-text-blue" aria-hidden="true" />
                )}
              </span>
            ),
            key: ScreenEnum.RestaurantOnboarding,
            icon: <ListChecks size={16} />,
          },
          {
            // Gift Cards lives under Marketing now, but store roles are not
            // granted that screen group — a counter user has no business in a
            // Marketing section, and widening their groups to reach one item
            // would be a permission change dressed up as a nav change. They keep
            // the entry here, exactly where it has always been. Everyone who can
            // open Marketing sees it there and not here, so it never doubles up.
            hidden: !company?.id || !showGiftCardsUnderHome,
            label: t('gift_cards.title'),
            key: ScreenEnum.GiftCards,
            icon: <Gift size={16} />,
          },
          {
            label: t('customer_base.title'),
            // Shown to PLG partners too (§5) — the portfolio overview. Still
            // only while no single restaurant is selected, as before.
            hidden: !!company?.id,
            key: ScreenEnum.CustomerBase,
            icon: <Users size={16} />,
          },
          {
            // Legacy batch-create page: masters keep it for their batch flow, and
            // partners on the new flow create restaurants only through the
            // onboarding wizard, so it is hidden for them to leave a single clean
            // "+ New Restaurant" entry (FOO-6547, founder feedback).
            //
            // Keyed on `isPlgPartner` (partner staff AND plg_onboarding) rather than
            // `!is_master`: a partner still on the classic flow gets no wizard, so
            // hiding this too would leave them no way to create a restaurant.
            hidden: isPlgPartner,
            label: t('new_company.title'),
            key: ScreenEnum.Companies,
            icon: <Store size={16} />,
          },
          {
            label: t('agent_ops_queue.title'),
            key: ScreenEnum.AgentOpsQueue,
            hidden: !can(ManagerPermission.ManageAiSuggestions),
            badge: {
              color: 'primary',
              text: badges?.[ScreenEnum.AgentOpsQueue] > 0 ? badges?.[ScreenEnum.AgentOpsQueue] : null,
            },
            icon: <Sparkles size={16} />,
          },
          {
            // Shown to PLG partners too: a partner's restaurants are organised
            // into groups by the onboarding wizard, and this is the only place
            // to see or edit one afterwards (rename, subdomain, members). It was
            // hidden alongside Customer base and External deliveries when the
            // PLG rail was first cut back; Customer base came back for the same
            // reason, and the WLP nav ticket then renamed this entry to
            // "Brands" for an audience that could not see it.
            label: t('franchises.title'),
            key: ScreenEnum.Franchises,
            icon: <Store size={16} />,
          },
          {
            hidden:
              isPlg ||
              availableDeliveryProviders(serviceProvider).length < 2 ||
              import.meta.env.VITE_APP_MODE !== ENVIRONMENTS.PROD,
            label: t('external_delivery_support.title'),
            key: ScreenEnum.ExternalDeliverySupport,
          },
          {
            label: t('payment_gateways.title'),
            key: ScreenEnum.PaymentGateways,
            badge: {
              color: 'warning',
              text: badges?.[ScreenEnum.PaymentGateways] > 0 ? badges?.[ScreenEnum.PaymentGateways] : null,
            },
            icon: <CreditCard size={16} />,
          },
          {
            // FoodAmigos-internal tooling: never shown to a partner, whatever the
            // partner Admin's permissions say. Global-access staff keep it while
            // acting on behalf of a partner, matching the polygraphy gate below
            // and HasMasterProviderGuard on the route. A super admin also keeps
            // it for a self-serve (PLG) partner, where the flags are the switch
            // needed while that partner's restaurants are being onboarded.
            hidden:
              (isPlg && !isSuperAdmin) ||
              !(serviceProvider?.is_master || hasGlobalAccess) ||
              !can(ManagerPermission.ReadFeatureFlags) ||
              !company?.id,
            label: t('feature_flags.title'),
            key: ScreenEnum.FeatureFlags,
            icon: <Flag size={16} />,
          },
          {
            // Our team's Amigo log (D8): what the platform agent did for every restaurant and what it couldn't do.
            // FoodAmigos team roles on the master provider only: canAccessScreen holds it to the PlatformAgent screen
            // group (no store roles) and MasterOnlyScreens (never on a partner's provider). It spans every restaurant,
            // so it shows with or without one selected.
            label: t('platform_agent_log.title'),
            key: ScreenEnum.PlatformAgentLog,
            icon: <Sparkles size={16} />,
          },
          {
            // Platform staff who see the partner catalogue: SuperAdmin, plus anyone
            // explicitly granted ReadPartners (the Admin wildcard excludes it, so a
            // partner's own Admin never sees this).
            hidden: !can(ManagerPermission.ReadPartners) || !serviceProvider?.is_master,
            label: t('partners.title'),
            key: ScreenEnum.Partners,
            icon: <Store size={16} />,
          },
          {
            // Every partner's non-billable test restaurant. The backend list
            // route and the page guard both ask for ManagePartners, which the
            // partner Admin wildcard excludes.
            hidden: !can(ManagerPermission.ManagePartners) || !serviceProvider?.is_master,
            label: t('test_restaurants.title'),
            key: ScreenEnum.TestRestaurants,
            icon: <FlaskConical size={16} />,
          },
          {
            hidden: !serviceProvider?.is_master,
            label: t('going_live.title'),
            children: [
              {
                label: t('going_live.children.setup_package_fulfillments'),
                key: ScreenEnum.SetupPackageFulfillments,
                badge: {
                  color: 'info',
                  text:
                    badges?.[ScreenEnum.SetupPackageFulfillments] > 0
                      ? badges?.[ScreenEnum.SetupPackageFulfillments]
                      : null,
                },
              },
              {
                hidden: !company?.id,
                label: t('going_live.children.polygraphy'),
                key: ScreenEnum.MarketingPolygraphyProducts,
              },
            ],
            icon: <Rocket size={16} />,
          },
          {
            hidden: isPlg,
            label: t('subscriptions.title'),
            children: [
              {
                hidden: !hasMerchantBilling,
                label: t('subscriptions.children.products'),
                key: ScreenEnum.SubscriptionProducts,
              },
            ],
            icon: <FileText size={16} />,
          },
          {
            label: t('sales.title'),
            children: [
              {
                label: t('sales.children.orders'),
                key: ScreenEnum.SalesOrders,
              },
              {
                // Restaurant-level only, like the rest of Sales.
                hidden: !company?.id,
                label: t('top_items.title'),
                key: ScreenEnum.TopItems,
              },
              {
                // End-customer receipts only exist for providers with the
                // issue_end_customer_receipts ("End-customer invoices") flag;
                // route guard: HasConsumerInvoicesGuard.
                hidden: !company?.id || !issuesEndCustomerReceipts,
                label: t('sales.children.receipts'),
                key: ScreenEnum.SalesReceipts,
              },
              {
                hidden: isPlg,
                label: t('sales.children.external_deliveries'),
                key: ScreenEnum.SalesExternalDeliveries,
              },
            ],
            icon: <BarChart3 size={16} />,
          },
          {
            label: t('billing.title'),
            icon: <FileText size={16} />,
            children: [
              {
                hidden: isPlg || !company?.id,
                label: t('billing.children.monthly_statements'),
                key: ScreenEnum.BillingMonthlyStatements,
              },
              {
                hidden: isPlg,
                label: t('billing.children.payouts'),
                key: ScreenEnum.BillingPayouts,
              },
              {
                hidden: isPlg || !company?.id || !hasMerchantBilling,
                label: t('billing.children.subscription'),
                key: ScreenEnum.BillingSubscription,
              },
              {
                hidden: isPlg || !hasMerchantBilling,
                label: t('billing.children.invoices'),
                key: ScreenEnum.BillingInvoices,
              },
              {
                // Per-restaurant only, so it needs a selected restaurant.
                hidden: !company?.id,
                label: t('billing.children.sales_summary'),
                key: ScreenEnum.BillingSalesSummary,
              },
              {
                // Provider-scoped (the backend reports the acting provider's own
                // restaurants), so PLG partners get it too once they bill their
                // restaurants through us. Route guard: HasMerchantBillingGuard.
                hidden: !hasMerchantBilling,
                label: t('billing.children.income_reports'),
                key: ScreenEnum.BillingIncomeReports,
              },
              {
                label: t('billing.children.tax_reports'),
                key: ScreenEnum.BillingTaxReports,
                hidden: isPlg || !serviceProvider?.is_master,
              },
            ],
          },
          {
            label: t('master_menu.title'),
            hidden: isFranchiseSyncHidden({
              hasSelectedFranchise,
              canWriteMasterMenu: can(ManagerPermission.WriteMasterMenu),
              // Only partners on the new onboarding flow; classic providers keep
              // exactly the visibility they have today.
              isPlgPartner: isPlgPartner,
              isMultiLocationBrand,
            }),
            icon: <UtensilsCrossed size={16} />,
            children: [
              {
                label: t('menu.children.master_overview'),
                key: ScreenEnum.MasterOverview,
              },
              {
                label: t('menu.children.manage_menus'),
                key: ScreenEnum.MasterMenu,
              },
              {
                label: t('menu.children.modifiers'),
                key: ScreenEnum.MasterModifiers,
              },
              {
                label: t('menu.children.smart_pricing'),
                key: ScreenEnum.MasterSmartPricing,
              },
              {
                label: t('menu.children.import'),
                key: ScreenEnum.MasterMenuImport,
              },
              {
                label: t('marketing.children.offers'),
                key: ScreenEnum.MasterOffers,
              },
            ],
          },
          {
            hidden: !company?.id,
            label: t('menu.title'),
            icon: <UtensilsCrossed size={16} />,
            children: [
              {
                label: t('menu.children.manage_menus'),
                key: ScreenEnum.Menu,
              },
              {
                label: t('menu.children.modifiers'),
                key: ScreenEnum.Modifiers,
              },
              {
                label: t('menu.children.smart_pricing'),
                key: ScreenEnum.SmartPricing,
              },
              {
                label: t('menu.children.sold_out_items'),
                key: ScreenEnum.SoldOutItems,
              },
              {
                label: t('menu.children.import'),
                key: ScreenEnum.MenuImport,
              },
              {
                label: t('menu.children.edit_history'),
                key: ScreenEnum.MenuEditHistory,
              },
              {
                label: t('marketing.children.offers'),
                key: ScreenEnum.Offers,
                hidden: !company?.id,
              },
            ],
          },
          {
            hidden: !company?.id,
            label: t('my_store.title'),
            icon: <Form size={16} />,
            children: [
              {
                hidden: !company?.id,
                label: t('my_store.children.settings'),
                key: ScreenEnum.MyStoreSettings,
              },
              {
                hidden: !company?.id,
                label: t('my_store.children.gallery'),
                key: ScreenEnum.MyStoreGallery,
              },
              {
                label: t('my_store.children.business_information'),
                key: ScreenEnum.MyStoreBusinessInfo,
              },
              {
                label: t('my_store.children.documents'),
                key: ScreenEnum.MyStoreDocuments,
                // Any role that may read documents sees them for every restaurant it
                // reaches — being the merchant's designated sales manager no longer
                // matters (PROD-524). Uploads and deletes are gated inside the view.
                hidden:
                  isPlg ||
                  !can([ManagerPermission.ReadBusinessProfilesDocs, ManagerPermission.ManageBusinessProfilesDocs]),
              },
              {
                label: t('my_store.children.personal_information'),
                key: ScreenEnum.MyStorePersonalInfo,
                // Collected on the provider's hosted onboarding page for PLG partners.
                hidden: isPlg,
              },
              {
                label: t('my_store.children.bank_info'),
                key: ScreenEnum.MyStoreBankInfo,
                // Collected on the provider's hosted onboarding page for PLG partners.
                hidden: isPlg,
              },
              {
                label: t('my_store.children.tax_info'),
                key: ScreenEnum.MyStoreTaxInfo,
                // Collected on the provider's hosted onboarding page for PLG partners.
                hidden: isPlg,
              },
              {
                label: t('my_store.children.shipping_info'),
                key: ScreenEnum.MyStoreShippingInfo,
                // Delivery address for the master-only polygraphy (print) service —
                // hidden for partner providers, except global-access staff.
                hidden: !(serviceProvider?.is_master || hasGlobalAccess),
              },
              {
                label: t('my_store.children.google_profile'),
                key: ScreenEnum.MyStoreGoogleProfile,
              },
              {
                hidden: isPlg || !canManageStoreSeo,
                label: t('my_store.children.seo'),
                key: ScreenEnum.MyStoreSeo,
              },
              {
                label: t('my_store.children.impressum'),
                key: ScreenEnum.MyStoreImpressum,
                // A PLG partner's restaurant pastes its own imprint instead (below);
                // nothing is generated for it from these fields. The selected
                // restaurant's provider decides too, as the route guards do, so a
                // master admin on such a restaurant is offered its Imprint instead.
                hidden: isPlg || selectedRestaurantIsPlg,
              },
              {
                label: t('my_store.children.imprint'),
                key: ScreenEnum.MyStoreImprint,
                hidden: !selectedRestaurantIsPlg,
              },
              {
                label: t('my_store.children.post_launch'),
                key: ScreenEnum.MyStorePostLaunch,
                hidden: isPlg || !can(ManagerPermission.ManageOnboarding),
              },
              {
                label: t('my_store.children.tax_currency'),
                key: ScreenEnum.MyStoreTaxCurrency,
                hidden: !can(ManagerPermission.WriteCompanySettings),
              },
            ],
          },
          {
            hidden: !company?.id,
            label: t('opening_hours.title'),
            icon: <Clock size={16} />,
            children: [
              {
                label: t('opening_hours.children.opening_hours'),
                key: ScreenEnum.OpeningHours,
              },
              {
                hidden: !company?.id,
                label: t('opening_hours.children.location'),
                key: ScreenEnum.Locations,
              },
            ],
          },
          {
            hidden: !company?.id,
            label: t('order_settings.title'),
            icon: <Settings size={16} />,
            children: [
              {
                label: t('order_settings.children.takeout'),
                key: ScreenEnum.OrderSettingsTakeout,
              },
              {
                hidden:
                  !handlesMissedOrdersAsPartner(company?.service_provider) ||
                  !can(ManagerPermission.WriteCompanySettings),
                label: t('order_settings.children.missed_orders'),
                key: ScreenEnum.OrderSettingsMissedOrders,
                badge: { color: 'green', text: t('new_tag') },
              },
              {
                label: t('order_settings.children.order_layout'),
                key: ScreenEnum.OrderSettingsOrderLayout,
              },
              {
                label: t('order_settings.children.payment_methods'),
                key: ScreenEnum.OrderSettingsPaymentMethods,
              },
              {
                hidden: !company?.enabled_feature_flags?.includes(FeatureFlags.PaypalStandalone),
                label: t('order_settings.children.connect_paypal'),
                key: ScreenEnum.OrderSettingsConnectPaypal,
              },
              {
                label: t('order_settings.children.tips'),
                key: ScreenEnum.OrderSettingsTipSettings,
              },
              {
                // The backend's resolved answer: always for a white-label partner,
                // behind the super-admin toggle for a legacy one.
                hidden:
                  !serviceProvider?.is_service_fee_change_allowed_per_restaurant ||
                  !can(ManagerPermission.WriteServiceFeeSettings),
                label: t('order_settings.children.service_fee'),
                key: ScreenEnum.OrderSettingsServiceFee,
              },
              {
                hidden: !managesStripeProcessingFees,
                label: t('order_settings.children.processing_fees'),
                key: ScreenEnum.OrderSettingsProcessingFees,
              },
              {
                label: t('order_settings.children.receipt'),
                key: ScreenEnum.OrderSettingsReceiptSettings,
              },
              {
                // Gated on the provider's issue_end_customer_receipts flag: without it
                // guests get no invoice option at checkout, so the auto-generation
                // toggle has nothing to act on (mirrors Company::canIssueEndCustomerReceipts
                // on the backend). Billing -> Invoices, the provider's own invoices to
                // the restaurant, is a separate item and is unaffected.
                hidden: isPlg || !issuesEndCustomerReceipts,
                label: t('order_settings.children.invoices'),
                key: ScreenEnum.OrderSettingsInvoicesSettings,
              },
              {
                label: t('order_settings.children.staff_credentials'),
                key: ScreenEnum.OrderSettingsStaffCredentials,
              },
              {
                label: t('order_settings.children.integrations'),
                key: ScreenEnum.OrderSettingsIntegrations,
              },
              {
                label: t('order_settings.children.terminal'),
                key: ScreenEnum.OrderSettingsTerminal,
              },
            ],
          },
          {
            hidden: isPlg || !serviceProvider?.has_merchant_contracts || !hasMerchantBilling,
            label: t('contract.title'),
            icon: <FileSignature size={16} />,
            children: [
              {
                hidden: !company?.id,
                label: t('contract.children.agreement'),
                key: ScreenEnum.ContractAgreement,
              },
              {
                label: t('contract.children.template'),
                key: ScreenEnum.ContractTemplate,
              },
            ],
          },
        ],
      },
      {
        // The partner's own section — its own rail entry rather than items inside
        // Online Shop, because none of it is about a restaurant. Visibility is
        // otherwise handled by canAccessScreen (PartnerAdminScreens): partner
        // (non-master) Admins only, so on the master provider every child is
        // denied and the group drops out on its own.
        //
        // Hidden wholesale for PLG partners while a restaurant is selected
        // (see `hidePlgPartnerTabs`) — with one selected the sidebar is about
        // that restaurant.
        key: ScreenGroupEnum.Partner,
        group: t('groups.partner'),
        icon: <Handshake size={16} />,
        hidden: hidePlgPartnerTabs,
        children: [
          {
            label: t('partner.children.account'),
            key: ScreenEnum.PartnerAccount,
          },
          {
            label: t('partner.children.branding'),
            key: ScreenEnum.PartnerBranding,
          },
          {
            label: t('partner.children.payment_gateway'),
            key: ScreenEnum.PartnerPaymentGateway,
          },
          {
            label: t('partner.children.billing'),
            key: ScreenEnum.PartnerBilling,
          },
          {
            label: t('partner.children.tax_currency'),
            key: ScreenEnum.PartnerTaxCurrency,
          },
          {
            label: t('partner.children.service_fee_calculator'),
            key: ScreenEnum.PartnerServiceFeeCalculator,
          },
          {
            hidden: !managesStripeProcessingFees,
            label: t('partner.children.processing_fees'),
            key: ScreenEnum.PartnerProcessingFees,
          },
        ],
      },
      {
        key: ScreenGroupEnum.Pricing,
        icon: <EuroCircleOutlined />,
        group: t('groups.pricing'),
        hidden:
          import.meta.env.VITE_APP_MODE === ENVIRONMENTS.DEV ||
          !serviceProvider?.is_master ||
          typeof company?.id === 'number',
      },
      {
        key: ScreenGroupEnum.ROI,
        group: t('groups.roi'),
        icon: <CalculatorOutlined />,
        hidden:
          import.meta.env.VITE_APP_MODE === ENVIRONMENTS.DEV ||
          !serviceProvider?.is_master ||
          typeof company?.id === 'number',
      },
      {
        key: ScreenGroupEnum.Marketing,
        group: t('groups.marketing'),
        icon: <MarketingIcon />,
        hidden: !(isSuperAdmin || isOnboardingManager || company?.id),
        children: [
          {
            label: t('marketing.children.overview'),
            key: ScreenEnum.MarketingOverview,
            hidden: !isSuperAdmin && !company?.id,
          },
          {
            label: t('marketing.children.followers'),
            key: ScreenEnum.MarketingFollowers,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.email_messages'),
            key: ScreenEnum.MarketingEmailMessages,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.push_notifications'),
            key: ScreenEnum.MarketingPushNotifications,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.promo_codes'),
            key: ScreenEnum.MarketingPromoCodes,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.google_review_program'),
            key: ScreenEnum.MarketingGoogleReviewProgram,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.google_business'),
            key: ScreenEnum.MarketingGoogleBusiness,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.google_ads'),
            key: ScreenEnum.MarketingGoogleAds,
            hidden: isPlg || (isOnboardingManager ? false : !company?.id),
          },
          {
            label: t('marketing.children.loyalty_program'),
            key: ScreenEnum.MarketingLoyaltyProgram,
            hidden: !company?.id,
          },
          {
            // Sits beside Loyalty — the other stored-value programme. Its route
            // stays under Online Shop (moving it to /marketing would take Gift
            // Cards away from store roles, who are not granted that group), so
            // the sidebar resolves this section by item key rather than by URL
            // prefix. See `findGroupForPath` in Sidebar.jsx.
            label: t('gift_cards.title'),
            key: ScreenEnum.GiftCards,
            hidden: !company?.id || showGiftCardsUnderHome,
          },
          {
            label: t('marketing.children.polygraphy'),
            key: ScreenEnum.MarketingPolygraphyProducts,
            // Foodamigos-operated print service — master provider only, except
            // global-access staff (mirrors the backend gate, which admits
            // hasAllProvidersAccess() while acting on behalf of a partner).
            hidden: !company?.id || !(serviceProvider?.is_master || hasGlobalAccess),
          },
          {
            label: t('marketing.children.tracking_links'),
            key: ScreenEnum.MarketingTrackingLinks,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.website_analytics'),
            key: ScreenEnum.MarketingWebsiteAnalytics,
            hidden: !company?.id,
          },
          {
            label: t('marketing.children.featured_posts'),
            key: ScreenEnum.MarketingFeaturedPosts,
            hidden: !company?.id,
          },
        ],
      },
      {
        key: ScreenGroupEnum.Reservations,
        group: t('groups.reservations'),
        icon: <TableIcon />,
        badge: {
          color: 'info',
          show: badges?.[ScreenEnum.ReservationsOverview] > 0,
        },
        children: [
          {
            label: t('reservations.children.overview'),
            key: ScreenEnum.ReservationsOverview,
            badge: {
              color: 'info',
              text: badges?.[ScreenEnum.ReservationsOverview] > 0 ? badges?.[ScreenEnum.ReservationsOverview] : null,
              overflowCount: 9,
            },
          },
          {
            label: t('reservations.children.booking_availability'),
            key: ScreenEnum.ReservationsBookingAvailability,
            hidden: !company?.id,
          },
          {
            label: t('reservations.children.settings'),
            key: ScreenEnum.ReservationsSettings,
            hidden: !company?.id,
          },
        ],
      },
      // {
      //   group: t('groups.phone_answering'),
      //   icon: <PhoneOutlined />,
      //   key: ScreenEnum.PhoneAnswering,
      //   hidden: !company?.id,
      // },
      {
        key: ScreenGroupEnum.Website,
        group: t('groups.website'),
        icon: <GlobalOutlined />,
        hidden: !company?.id,
        children: [
          {
            label: t('website.children.storefront'),
            key: ScreenEnum.WebsiteStorefront,
          },
          {
            label: t('website.children.studio'),
            key: ScreenEnum.WebsiteStudio,
            hidden: !isWebsiteStudioAvailable(serviceProvider) || !can(ManagerPermission.ManageWebsiteStudio),
          },
          {
            // Website Studio v2 next to v1 until every v1 project is migrated (DW1).
            label: t('website.children.studio_v2'),
            key: ScreenEnum.WebsiteStudioV2,
            hidden: !isWebsiteStudioAvailable(serviceProvider) || !can(ManagerPermission.ManageWebsiteStudio),
          },
        ],
      },
      {
        key: ScreenGroupEnum.MobileApp,
        icon: (
          <div className="border border-solid border-current w-[16px] h-[16px] rounded-[4px] flex items-center justify-center">
            <CrownOutlined className="p-none m-[2px]" style={{ fontSize: 9 }} />
          </div>
        ),
        group: t('groups.mobile_app'),
        children: [
          {
            label: t('mobile_app.children.overview'),
            key: ScreenEnum.MobileAppOverview,
          },
          {
            label: t('mobile_app.children.updates'),
            key: ScreenEnum.MobileAppUpdates,
            hidden: Boolean(company?.id),
          },
          {
            label: t('mobile_app.children.intro_screen'),
            key: ScreenEnum.MobileAppIntroScreen,
            hidden: !company?.id,
          },
        ],
      },
      {
        key: ScreenGroupEnum.OrdersPortal,
        group: t('groups.orders_portal'),
        icon: <TabletOutlined />,
        hidden: !company?.id,
      },
      {
        key: ScreenGroupEnum.Team,
        icon: <TeamOutlined />,
        group: t('groups.team'),
        hidden: isMerchant && !hasSelectedFranchise,
        children: [
          {
            // Admins manage their own provider's team (partner admins included).
            hidden: !isAdmin && !hasSelectedFranchise,
            label: t('team.children.managers'),
            key: ScreenEnum.TeamManagers,
          },
          {
            // Sales-team reports are platform-wide — master staff only
            // (the backend master-gates the endpoint as well).
            hidden: isPlg || !serviceProvider?.is_master,
            label: t('team.children.reports'),
            key: ScreenEnum.TeamReports,
          },
        ],
      },
      {
        // Icon-only rail entry pinned to the bottom: a partner-level surface that
        // does not belong to any one shop section. Keyed by the screen rather than a
        // group, so selecting it navigates and leaves the section panel untouched.
        placement: 'bottom',
        key: ScreenGroupEnum.Developers,
        icon: <Code2 size={20} />,
        group: t('developers.title'),
        hidden: !can(ManagerPermission.ReadDeveloperSettings),
      },
      {
        key: ScreenGroupEnum.DataDeletionRequests,
        icon: <DeleteOutlined />,
        group: t('groups.data_deletion_requests'),
        // Admins see the entry at all times; other managers only within a selected company.
        //
        // Dropped for partners on self-serve (PLG) onboarding only: actioning an
        // end user's deletion request is a FoodAmigos obligation they are not set
        // up to carry. Partners on the classic setup keep it — they have run this
        // themselves since before the flag existed, and taking it away would strand
        // their pending requests. Route guard: HasNotPlgOnboardingGuard.
        hidden: isPlg || (!isAdmin && !company?.id),
        badge: {
          color: 'red',
          show: badges?.[ScreenEnum.DataDeletionRequests] > 0,
        },
      },
      {
        // Last entry of the rail: FoodAmigos staff report tech issues and follow
        // every ticket's status. Only while the master service provider
        // (FoodAmigos itself) is active, and only for FoodAmigos team roles
        // (RoleScreenGroups); MasterOnlyScreens keeps partner SPs out of the
        // routes too. Both pages work without a selected restaurant: the report
        // page then asks for one.
        placement: 'bottom',
        key: ScreenGroupEnum.ReportTechIssue,
        icon: <Bug size={20} />,
        // FoodAmigos-internal, English only (see views/report-tech-issue/strings.js).
        group: 'Tech issues',
        hidden: !serviceProvider?.is_master,
        children: [
          {
            // FoodAmigos-internal, English only (see views/report-tech-issue/strings.js).
            label: 'Report an issue',
            key: ScreenEnum.ReportTechIssue,
          },
          {
            label: 'Overview',
            key: ScreenEnum.ReportTechIssueOverview,
          },
        ],
      },
    ],
    [
      t,
      company,
      selectedRestaurantIsPlg,
      badges,
      serviceProvider,
      hasMerchantBilling,
      isPlg,
      hidePlgPartnerTabs,
      isSuperAdmin,
      isAdmin,
      hasSelectedFranchise,
      isMultiLocationBrand,
      can,
      isOnboardingManager,
      isMerchant,
      isPlgPartner,
      isLive,
      canManageStoreSeo,
      hasGlobalAccess,
      showGiftCardsUnderHome,
      isOnboardingComplete,
      showsGetStartedAfterOnboarding,
      managesStripeProcessingFees,
    ],
  );

  function filterCallback(item, parent) {
    return navItemIsVisible(item, parent, { canAccessScreen, canAccessScreenGroup });
  }

  function filterItems(items, parent = null) {
    return collect(items)
      .filter((item) => filterCallback(item, parent))
      .map((item) => {
        if (item.children) {
          return {
            ...item,
            children: filterItems(item.children, item),
          };
        }

        return {
          ...item,
          label: item?.badge ? (
            <Link to={item.key} className="block hover:no-underline">
              {item.label}
              {item.badge && (
                <Badge
                  count={item.badge.text}
                  color={item.badge.color}
                  overflowCount={item.badge.overflowCount ?? 9999999}
                  className="ml-xs"
                />
              )}
            </Link>
          ) : (
            <Link to={item.key} className="block hover:no-underline">
              {item.label}
            </Link>
          ),
        };
      })
      .toArray();
  }

  return useMemo(() => filterItems(items), [roles, isOnboardingComplete, i18n.language, items]);
}
